import React, { Component } from 'react';
import { connect, ResolveThunks } from 'react-redux';
import { Box, Checkbox, FormControl, FormControlLabel, InputLabel, MenuItem, Select, TextField } from '@mui/material';

import { Annotation, AnnotationTemplateType } from '@paragon/contract-library';
import { hasOrganizationTemplatesPermission } from '@paragon/login-tools';
import { enqueueError, enqueueWarning } from '@paragon/notification-tools';
import { closeCurrentPopup, Dialog } from '@paragon/popup-state';
import { humanCase } from '@paragon/utilities';

import { addAnnotationTemplate } from '../../actions/annotationTemplates';
import { getSortedTemplateGroupNames } from '../../selectors/annotationTemplates';
import { RootState } from '../../store';

const contentDivSx = { width: '100%', padding: 1 } as const;
const contentSx = { width: '100%' } as const;

const annotationTemplateTypeOptions: AnnotationTemplateType[] = [
  'Other',
  'TrussDesignDrawing',
  'TrussDesignDrawingOverflow',
  'RepairTrussDesignDrawing',
];

const UNASSIGNED_LABEL = '(unassigned)';
const CREATE_GROUP_LABEL = '(create group)';

type OwnProps = {
  selectedAnnotations: readonly Annotation[];
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ResolveThunks<typeof actionCreators>;
type Props = OwnProps & StateProps & DispatchProps;

type OwnState = {
  name: string;
  group: string;
  isOrganizationTemplate: boolean;
  annotationTemplateType: AnnotationTemplateType;
};

class NewAnnotationTemplate extends Component<Props, OwnState> {
  state: OwnState = {
    name: '',
    group: UNASSIGNED_LABEL,
    isOrganizationTemplate: false,
    annotationTemplateType: 'Other',
  };

  onSubmitClick = () => {
    const templateName = this.state.name;

    if (templateName.length === 0) {
      this.props.enqueueError('Failed to save annotation template: name was left empty.');
      return;
    }

    const group = this.getSelectedGroup();
    const { selectedAnnotations } = this.props;

    this.props.addAnnotationTemplate(
      templateName,
      group,
      selectedAnnotations,
      this.state.isOrganizationTemplate,
      this.state.annotationTemplateType,
    );
    this.props.closeCurrentPopup();
  };

  getSelectedGroup() {
    if (this.state.group === UNASSIGNED_LABEL) {
      return null;
    }

    if (this.state.group === CREATE_GROUP_LABEL) {
      const newGroupName = prompt(
        'Please enter a name for the new group. Your template will automatically be added to this group.',
      );

      if (newGroupName == null || newGroupName.length === 0) {
        this.props.enqueueWarning('Warning: no group name given. Template will not be assigned to a group.');
        return null;
      }

      return newGroupName;
    }

    return this.state.group;
  }

  render() {
    const groupNames = [CREATE_GROUP_LABEL].concat(this.props.templateGroups);
    const { hasOrganizationTemplatesPermission } = this.props;

    return (
      <Dialog
        title="New Annotation Template"
        submitText="Save Annotation Template"
        onSubmit={this.onSubmitClick}
        contentSx={{ display: 'flex', flexDirection: 'column' }}
      >
        <Box sx={contentDivSx}>
          <TextField
            onChange={(event) => void this.setState({ name: event.target.value as string })}
            sx={contentSx}
            variant="filled"
            fullWidth
            margin="normal"
            autoFocus
            label="New Template Name"
          />
        </Box>
        {hasOrganizationTemplatesPermission && (
          <Box sx={contentDivSx}>
            <FormControlLabel
              control={
                <Checkbox
                  color="default"
                  checked={this.state.isOrganizationTemplate}
                  disabled={!hasOrganizationTemplatesPermission}
                  onChange={(_, checked) => void this.setState({ isOrganizationTemplate: checked })}
                />
              }
              label="Make organization template"
            />
          </Box>
        )}
        <Box sx={contentDivSx}>
          <FormControl sx={contentSx}>
            <InputLabel htmlFor="annotation-template-type">Template Type</InputLabel>
            <Select
              value={this.state.annotationTemplateType}
              onChange={(event) =>
                void this.setState({ annotationTemplateType: event.target.value as AnnotationTemplateType })
              }
              id="annotation-template-type"
              label="Template Type"
            >
              {annotationTemplateTypeOptions.map((annotationTemplateType) => (
                <MenuItem value={annotationTemplateType} key={annotationTemplateType}>
                  {humanCase(annotationTemplateType)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
        <Box sx={contentDivSx}>
          <FormControl sx={contentSx}>
            <InputLabel htmlFor="group-name">Assign to Group</InputLabel>
            <Select
              value={this.state.group}
              onChange={(event) => void this.setState({ group: event.target.value as string })}
              id="group-name"
              label="Assign to Group"
            >
              <MenuItem id="unassigned" value={UNASSIGNED_LABEL}>
                {UNASSIGNED_LABEL}
              </MenuItem>
              {groupNames.map((group) => (
                <MenuItem value={group} key={group}>
                  {group}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Dialog>
    );
  }
}

const mapStateToProps = (state: RootState) => ({
  templateGroups: getSortedTemplateGroupNames(state),
  hasOrganizationTemplatesPermission: hasOrganizationTemplatesPermission(state),
});

const actionCreators = {
  addAnnotationTemplate,
  closeCurrentPopup,
  enqueueWarning,
  enqueueError,
};

export default connect(mapStateToProps, actionCreators)(NewAnnotationTemplate);
